import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { categoryService } from '../services/categoryService';

const CategoryTreeSelector = ({ 
  open, 
  onClose, 
  onSelect, 
  selectedCategory = null,
  title = "Select Category"
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [currentPath, setCurrentPath] = useState([]);
  const [currentLevel, setCurrentLevel] = useState([]);
  const [previewCategory, setPreviewCategory] = useState(null);
  const [loadedChildren, setLoadedChildren] = useState(new Map());
  const [loadingNodes, setLoadingNodes] = useState(new Set());

  // Load categories when dialog opens
  useEffect(() => {
    if (open) {
      loadCategories();
      setCurrentPath([]);
      setExpandedNodes(new Set());
      setSearchTerm('');
      setPreviewCategory(null);
    }
  }, [open]);

  // Update current level when children are loaded or path changes
  useEffect(() => {
    if (currentPath.length > 0) {
      const currentCategory = currentPath[currentPath.length - 1];
      const categoryId = currentCategory.sid || currentCategory.id;
      const children = loadedChildren.get(categoryId) || [];
      if (children.length > 0) {
        setCurrentLevel(children);
      }
    } else if (currentPath.length === 0 && categories.length > 0) {
      // At top level, show root categories
      setCurrentLevel(categories);
    }
  }, [loadedChildren, currentPath, categories]);

  // Load categories (top-level only)
  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getCategoryTreeLazy();
      
      if (response.success) {
        // Handle the double-nested response structure
        const categoriesData = response.data?.data || response.data || [];
        const safeCategories = Array.isArray(categoriesData) ? categoriesData : [];
        
        setCategories(safeCategories);
        setCurrentLevel(safeCategories);
        setLoadedChildren(new Map());
      } else {
        setError('Failed to load categories');
        setCategories([]);
        setCurrentLevel([]);
      }
    } catch (err) {
      setError('Error loading categories: ' + err.message);
      setCategories([]);
      setCurrentLevel([]);
    } finally {
      setLoading(false);
    }
  };

  // Load children of a specific category
  const loadCategoryChildren = async (categorySid) => {
    if (loadedChildren.has(categorySid)) {
      return;
    }

    try {
      setLoadingNodes(prev => new Set(prev).add(categorySid));
      
      const response = await categoryService.getCategoryChildren(categorySid);
      if (response.success) {
        // Handle the double-nested response structure
        const childrenData = response.data?.data || response.data || [];
        const safeChildren = Array.isArray(childrenData) ? childrenData : [];
        setLoadedChildren(prev => new Map(prev).set(categorySid, safeChildren));
      }
    } catch (error) {
      // Silently handle error - children loading is not critical
    } finally {
      setLoadingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(categorySid);
        return newSet;
      });
    }
  };

  // Filter categories based on search term
  const filterCategories = (categories, searchTerm) => {
    if (!searchTerm.trim()) return categories;
    
    const filtered = [];
    const searchLower = searchTerm.toLowerCase();
    
    const searchInCategory = (category) => {
      if (category.name.toLowerCase().includes(searchLower)) {
        filtered.push(category);
      }
      if (category.children && Array.isArray(category.children)) {
        category.children.forEach(child => searchInCategory(child));
      }
    };
    
    categories.forEach(category => searchInCategory(category));
    return filtered;
  };

  // Get current level categories (filtered by search if applicable)
  const getCurrentLevelCategories = () => {
    let categoriesToShow = currentLevel;
    
    if (searchTerm.trim()) {
      categoriesToShow = filterCategories(categories, searchTerm);
    }
    
    return categoriesToShow.sort((a, b) => {
      // Sort by display_order if available, otherwise by name
      if (a.display_order !== undefined && b.display_order !== undefined) {
        return a.display_order - b.display_order;
      }
      return a.name.localeCompare(b.name);
    });
  };

  // Handle double-click to navigate
  const handleDoubleClick = (category) => {
    const hasChildren = category.has_children || (category.children_count > 0);
    if (hasChildren) {
      navigateToCategory(category);
    }
  };

  // Navigate back to parent
  const navigateBack = () => {
    if (currentPath.length > 0) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      
      if (newPath.length === 0) {
        setCurrentLevel(categories);
      } else {
        // Get children from loadedChildren cache
        const parentCategory = newPath[newPath.length - 1];
        const children = loadedChildren.get(parentCategory.sid || parentCategory.id) || [];
        setCurrentLevel(children);
      }
    }
  };

  // Preview a category (click to preview for selection)
  const handlePreviewCategory = (category) => {
    setPreviewCategory(category);
  };

  // Navigate to a category (double-click or click on arrow)
  const navigateToCategory = async (category) => {
    const hasChildren = category.has_children || (category.children_count && category.children_count > 0);
    
    if (hasChildren) {
      const categoryId = category.sid || category.id;
      // Load children if not already loaded
      const children = loadedChildren.get(categoryId) || [];
      if (children.length === 0) {
        // Load children and then navigate
        setCurrentPath(prev => [...prev, category]);
        setExpandedNodes(prev => new Set([...prev, category.id]));
        setPreviewCategory(null);
        await loadCategoryChildren(categoryId);
      } else {
        setCurrentPath(prev => [...prev, category]);
        setCurrentLevel(children);
        setExpandedNodes(prev => new Set([...prev, category.id]));
        setPreviewCategory(null);
      }
    }
  };

  // Select a category (only if it has no children or user explicitly wants to select it)
  const selectCategory = (category) => {
    const hasChildren = category.has_children || (category.children_count && category.children_count > 0);
    
    if (hasChildren) {
      // If category has children, navigate to it
      navigateToCategory(category);
    } else {
      // If category has no children, select it directly
      onSelect(category);
      onClose();
    }
  };

  // Force select a category (regardless of children)
  const forceSelectCategory = (category) => {
    onSelect(category);
    onClose();
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Get breadcrumb path
  const getBreadcrumbPath = () => {
    const pathNames = currentPath.map(cat => cat.name);
    return pathNames.length > 0 ? pathNames.join(' > ') : 'All Categories';
  };

  // Check if category is selected
  const isCategorySelected = (category) => {
    return selectedCategory && selectedCategory.id === category.id;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          height: '80vh',
          // Slide in from right animation
          animation: 'slideInFromRight 0.3s ease-out',
          '@keyframes slideInFromRight': {
            '0%': {
              transform: 'translateX(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Search Bar */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            placeholder="Search categories..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Breadcrumb */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setCurrentPath([]);
                setCurrentLevel(categories);
                setPreviewCategory(null);
              }}
              sx={{ textTransform: 'none' }}
            >
              All Categories
            </Button>
            {currentPath.map((category, index) => (
              <React.Fragment key={category.id}>
                <ChevronRightIcon fontSize="small" />
                <Button
                  size="small"
                  onClick={() => {
                    const newPath = currentPath.slice(0, index + 1);
                    setCurrentPath(newPath);
                    // Get children from loadedChildren cache
                    const parentCategory = newPath[newPath.length - 1];
                    const children = loadedChildren.get(parentCategory.sid || parentCategory.id) || [];
                    setCurrentLevel(children);
                    setPreviewCategory(null);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {category.name}
                </Button>
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Category List */}
        {!loading && !error && (
          <List sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            {getCurrentLevelCategories().map((category) => {
              const hasChildren = category.has_children || (category.children_count > 0);
              const isSelected = isCategorySelected(category);
              const isPreviewed = previewCategory && previewCategory.id === category.id;
              
              return (
                <ListItem key={category.id} disablePadding>
                  <ListItemButton
                    onClick={() => selectCategory(category)}
                    selected={isSelected}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        },
                      },
                    }}
                  >
                    <ListItemIcon>
                      {hasChildren ? (
                        <FolderIcon color="primary" />
                      ) : (
                        <FolderOpenIcon color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={category.name}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          {hasChildren && (
                            <Chip
                              label={`${category.children_count || 0} subcategories`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                          {isSelected && (
                            <Chip
                              label="Selected"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      }
                    />
                    {hasChildren && (
                      loadingNodes.has(category.sid || category.id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ChevronRightIcon color="action" />
                      )
                    )}
                  </ListItemButton>
                  {/* Preview button for categories with children */}
                  {hasChildren && (
                    <Box sx={{ position: 'absolute', right: 40, top: '50%', transform: 'translateY(-50%)' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreviewCategory(category);
                        }}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Use
                      </Button>
                    </Box>
                  )}
                </ListItem>
              );
            })}
            
            {getCurrentLevelCategories().length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No categories found"
                  secondary={searchTerm ? "Try adjusting your search terms" : "No categories available"}
                />
              </ListItem>
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        {currentPath.length > 0 && (
          <Button onClick={navigateBack} variant="outlined">
            Back
          </Button>
        )}
        {previewCategory && (
          <Button 
            onClick={() => forceSelectCategory(previewCategory)} 
            variant="contained"
            color="primary"
            sx={{ ml: 'auto' }}
          >
            Use "{previewCategory.name}"
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CategoryTreeSelector;
